import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaceLandmarker } from '@mediapipe/tasks-vision';
import type { EyeTraxSample } from '../types';
import { useSession } from '../store/session';
import { loadGalleryConfig, MediaItem } from '../lib/gallery';
import { getFaceLandmarker } from '../lib/face';
import {
  computeFaceBoundsList,
  pickFaceIndexByAnchor,
  pickLargestFaceIndex,
  type FaceAnchor,
} from '../lib/faceSelection';
import { Button } from '@mui/material';
import { submitForm } from '../api';
import { openOrCreateDB, saveVideoArtifact, MyDB } from '../runner/openIDB';
import { toPositiveInt } from '../runner/uploadUtils';
import type { IDBPDatabase } from 'idb';
import {
  createRoiAccumulator,
  finalizeRoiAccumulator,
  updateRoiAccumulator,
  type RoiAccumulator,
} from '../runner/roi';
import { playAudio, stopAudio } from '../utils/audio';
import { getOrCreateClientRunId } from '../lib/clientRun';

const DEBUG_ENABLED = process.env.NODE_ENV !== 'production';
const CAPTURE_DEBUG_FLAG = String(import.meta.env.VITE_CAPTURE_DEBUG ?? '').toLowerCase();
const CAPTURE_DEBUG =
  CAPTURE_DEBUG_FLAG === '1' || CAPTURE_DEBUG_FLAG === 'true' || CAPTURE_DEBUG_FLAG === 'yes';
const STREAM_DURING_TEST_FLAG = String(
  import.meta.env.VITE_UPLOAD_STREAM_DURING_TEST ?? '',
).toLowerCase();
const STREAM_UPLOAD_DURING_TEST =
  STREAM_DURING_TEST_FLAG === '1' ||
  STREAM_DURING_TEST_FLAG === 'true' ||
  STREAM_DURING_TEST_FLAG === 'yes';
const DB_NAME = 'my-db';
const IDB_BATCH_SIZE = toPositiveInt(import.meta.env.VITE_UPLOAD_BATCH_SIZE, 250);
const FLUSH_INTERVAL_MS = toPositiveInt(import.meta.env.VITE_UPLOAD_FLUSH_INTERVAL_MS, 1000);
const MAX_WRITE_RETRIES = 5;
const WRITE_RETRY_DELAY_MS = 200;
const WORKER_STOP_TIMEOUT_MS = 2000;

type WorkerMessage = { type?: string; [key: string]: unknown };
type PendingRecord = MyDB['landmarks']['value'];

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const pickRecordingMimeType = (): string | undefined => {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
  ];
  for (const candidate of candidates) {
    try {
      if (typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    } catch {
      // ignore
    }
  }
  return undefined;
};

const logDebug = (message: string, payload?: unknown) => {
  if (!DEBUG_ENABLED) return;
  if (payload !== undefined) {
    console.debug(message, payload);
  } else {
    console.debug(message);
  }
};

export default function Gallery() {
  const nav = useNavigate();
  const CAPTURE_MODE = String(import.meta.env.VITE_CAPTURE_MODE ?? 'roi').toLowerCase();
  const CLIENT_ROI_MODE =
    CAPTURE_MODE === 'roi' || CAPTURE_MODE === 'client' || CAPTURE_MODE === 'client-roi';
  const stream = useSession((s) => s.stream);
  const hasPermission = useSession((s) => s.hasPermission);
  const faceOK = useSession((s) => s.faceOK);
  const setFaceOK = useSession((s) => s.setFaceOK);
  const selectedFaceAnchor = useSession((s) => s.selectedFaceAnchor);
  const setSelectedFaceAnchor = useSession((s) => s.setSelectedFaceAnchor);
  const addSample = useSession((s) => s.addSample);
  const setVideoArtifact = useSession((s) => s.setVideoArtifact);
  const startTimelineIfNeeded = useSession((s) => s.startTimelineIfNeeded);
  const startSegment = useSession((s) => s.startSegment);
  const endActiveSegment = useSession((s) => s.endActiveSegment);
  const [items, setItems] = useState<MediaItem[] | null>(null);
  const [idx, setIdx] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [formDataId, setFormDataId] = useState<number | null>(null);
  const [dbReady, setDbReady] = useState(false);

  const mediaImg = useRef<HTMLImageElement>(null);
  const mediaVid = useRef<HTMLVideoElement>(null);
  const cam = useRef<HTMLVideoElement>(null);
  const landmarkerRef = useRef<FaceLandmarker>();
  const workerRef = useRef<Worker | null>(null);
  const dbRef = useRef<IDBPDatabase<MyDB> | null>(null);
  const formIdRef = useRef<number | null>(null);
  const queueRef = useRef<PendingRecord[]>([]);
  const flushPromiseRef = useRef<Promise<void> | null>(null);
  const roiAccRef = useRef<RoiAccumulator>(createRoiAccumulator());
  const anchorRef = useRef<FaceAnchor | null>(selectedFaceAnchor);
  const faceOkRef = useRef(faceOK);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  const recorderMimeRef = useRef<string | null>(null);

  useEffect(() => {
    anchorRef.current = selectedFaceAnchor;
  }, [selectedFaceAnchor]);

  useEffect(() => {
    faceOkRef.current = faceOK;
  }, [faceOK]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (!root || !body) return;
    root.classList.add('hide-cursor');
    body.classList.add('hide-cursor');
    return () => {
      root.classList.remove('hide-cursor');
      body.classList.remove('hide-cursor');
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem('iris_roi_summary');
      localStorage.removeItem('argus_roi_summary');
    } catch (error) {
      logDebug('[Gallery] failed to clear iris_roi_summary', error);
    }
    setVideoArtifact(null);
  }, [setVideoArtifact]);

  useEffect(() => {
    if (!hasPermission) nav('/');
  }, [hasPermission, nav]);

  useEffect(() => {
    let mounted = true;
    loadGalleryConfig()
      .then((cfg) => mounted && setItems(cfg.media))
      .catch((e) => setErr(String(e)));
    getFaceLandmarker().then((lm) => {
      if (mounted) landmarkerRef.current = lm;
    });
    return () => {
      mounted = false;
    };
  }, []);

  const finalizeRecording = useCallback(() => {
    const chunks = recorderChunksRef.current;
    const mimeType = recorderMimeRef.current || chunks[0]?.type || 'video/webm';
    const blob = chunks.length > 0 ? new Blob(chunks, { type: mimeType }) : null;
    setVideoArtifact(blob, mimeType);
    if (blob && blob.size > 0) {
      void saveVideoArtifact(getOrCreateClientRunId(), blob, mimeType).catch((error) =>
        logDebug('[Gallery] failed to persist video artifact', error),
      );
    }
    recorderChunksRef.current = [];
    recorderMimeRef.current = blob ? mimeType : null;
  }, [setVideoArtifact]);

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    if (recorder.state === 'inactive') {
      recorderRef.current = null;
      finalizeRecording();
      return;
    }
    await new Promise<void>((resolve) => {
      recorder.addEventListener(
        'stop',
        () => {
          recorderRef.current = null;
          finalizeRecording();
          resolve();
        },
        { once: true },
      );
      recorder.stop();
    });
  }, [finalizeRecording]);

  useEffect(() => {
    if (!stream) return;
    if (typeof MediaRecorder === 'undefined') {
      setErr('当前浏览器不支持完整视频采集，请更换为较新的 Chrome、Edge 或 Safari。');
      return;
    }
    if (recorderRef.current) return;

    try {
      const mimeType = pickRecordingMimeType();
      const options = mimeType ? { mimeType, videoBitsPerSecond: 1_200_000 } : { videoBitsPerSecond: 1_200_000 };
      const recorder = new MediaRecorder(stream, options);
      recorderChunksRef.current = [];
      recorderMimeRef.current = mimeType ?? recorder.mimeType ?? null;
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) {
          recorderChunksRef.current.push(event.data);
        }
      });
      recorder.addEventListener('error', () => {
        setErr('视频录制失败，请重新开始采集。');
      });
      recorder.start(1000);
      recorderRef.current = recorder;
    } catch (error) {
      logDebug('[Gallery] failed to start video recording', error);
      setErr('无法启动完整视频录制，请检查浏览器权限后重试。');
    }

    return () => {
      const active = recorderRef.current;
      if (active && active.state !== 'inactive') {
        try {
          active.stop();
        } catch (error) {
          logDebug('[Gallery] failed to stop recorder during cleanup', error);
        }
      }
    };
  }, [stream]);

  const setCamRef = useCallback(
    (el: HTMLVideoElement | null) => {
      cam.current = el;
      if (el && stream) {
        if (el.srcObject !== stream) {
          el.srcObject = stream;
        }
        el.play().catch((error) => logDebug('[Gallery] autoplay attach failed', error));
      }
    },
    [stream],
  );

  useEffect(() => {
    const el = cam.current;
    if (!stream || !el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    if (el.paused) {
      el.play().catch((error) => logDebug('[Gallery] autoplay resume failed', error));
    }
  }, [stream]);

  const writeChunkToIDB = useCallback(
    async (records: PendingRecord[], attempt = 0): Promise<void> => {
      if (!records.length) return;
      let db = dbRef.current;
      if (!db) {
        try {
          db = await openOrCreateDB();
          dbRef.current = db;
          setDbReady(true);
        } catch (error) {
          if (attempt + 1 >= MAX_WRITE_RETRIES) {
            throw error instanceof Error ? error : new Error(String(error));
          }
          await delay(WRITE_RETRY_DELAY_MS);
          return writeChunkToIDB(records, attempt + 1);
        }
      }
      if (!db) return;
      try {
        const tx = db.transaction('landmarks', 'readwrite');
        const store = tx.objectStore('landmarks');
        for (const record of records) {
          await store.put(record);
        }
        await tx.done;
        if (CAPTURE_DEBUG) {
          console.debug('[capture-debug] wrote records to IDB', {
            count: records.length,
            formDataId: records[0]?.formDataId,
          });
        }
      } catch (error) {
        const isNotFound = error instanceof DOMException && error.name === 'NotFoundError';
        if (isNotFound) {
          try {
            db.close();
          } catch (closeErr) {
            logDebug('[Gallery] failed to close IDB after NotFoundError', closeErr);
          }
          dbRef.current = null;
          try {
            if (typeof indexedDB !== 'undefined') {
              indexedDB.deleteDatabase(DB_NAME);
            }
          } catch (delErr) {
            logDebug('[Gallery] failed to delete IDB', delErr);
          }
        }
        if (attempt + 1 >= MAX_WRITE_RETRIES) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        await delay(WRITE_RETRY_DELAY_MS);
        return writeChunkToIDB(records, attempt + 1);
      }
    },
    [setDbReady],
  );

  const flushQueue = useCallback(
    (flushAll = false): Promise<void> => {
      const execute = async () => {
        do {
          const queue = queueRef.current;
          if (!queue.length) break;
          const count = flushAll ? queue.length : Math.min(queue.length, IDB_BATCH_SIZE);
          const chunk = queue.splice(0, count);
          if (!chunk.length) break;
          try {
            await writeChunkToIDB(chunk);
            if (CAPTURE_DEBUG) {
              console.debug('[capture-debug] flushed queue chunk', {
                chunkSize: chunk.length,
                remaining: queueRef.current.length,
              });
            }
          } catch (error) {
            queue.unshift(...chunk);
            throw error;
          }
        } while (flushAll);
      };

      const base = flushPromiseRef.current ?? Promise.resolve();
      const next = base.then(execute);
      flushPromiseRef.current = next.finally(() => {
        if (flushPromiseRef.current === next) {
          flushPromiseRef.current = null;
        }
      });
      return flushPromiseRef.current;
    },
    [writeChunkToIDB],
  );

  const stopWorker = useCallback(async (timeoutMs = WORKER_STOP_TIMEOUT_MS) => {
    const worker = workerRef.current;
    if (!worker) return;
    const donePromise = new Promise<void>((resolve) => {
      let done = false;
      const onMessage = (event: MessageEvent<WorkerMessage>) => {
        if (event.data?.type === 'done') {
          done = true;
          worker.removeEventListener('message', onMessage as EventListener);
          window.clearTimeout(timer);
          resolve();
        }
      };
      worker.addEventListener('message', onMessage as EventListener);
      const timer = window.setTimeout(() => {
        if (!done) {
          worker.removeEventListener('message', onMessage as EventListener);
          resolve();
        }
      }, timeoutMs);
    });
    try {
      worker.postMessage({ type: 'stop' });
    } catch (error) {
      logDebug('[Gallery] failed to post stop message', error);
    }
    await donePromise;
    try {
      worker.terminate();
    } catch (error) {
      logDebug('[Gallery] failed to terminate worker', error);
    }
    if (workerRef.current === worker) {
      workerRef.current = null;
    }
  }, []);

  const startWorker = useCallback(
    (fdId: number) => {
      if (workerRef.current) return;
      try {
        const worker = new Worker(new URL('../runner/uploadWorker.ts', import.meta.url), {
          type: 'module',
        });
        const handleMessage = (event: MessageEvent<WorkerMessage>) => {
          const eventType = event.data?.type;
          if (eventType === 'done') {
            worker.removeEventListener('message', handleMessage as EventListener);
            return;
          }
          if (eventType === 'auth-error') {
            worker.removeEventListener('message', handleMessage as EventListener);
            if (workerRef.current === worker) {
              workerRef.current = null;
            }
            try {
              worker.terminate();
            } catch (error) {
              logDebug('[Gallery] failed to terminate worker after auth error', error);
            }
            setErr('登录状态已过期，请重新登录。');
          }
        };
        worker.addEventListener('message', handleMessage as EventListener);
        workerRef.current = worker;
        worker.postMessage({
          type: 'start',
          formDataId: fdId,
          debug: CAPTURE_DEBUG,
          clientRunId: getOrCreateClientRunId(),
        });
      } catch (error) {
        logDebug('[Gallery] failed to start upload worker', error);
        setErr('无法启动数据上传，请刷新页面重试。');
      }
    },
    [setErr],
  );

  useEffect(() => {
    if (CLIENT_ROI_MODE) return;
    let cancelled = false;
    let intervalId: number | null = null;

    (async () => {
      try {
        const db = await openOrCreateDB();
        if (cancelled) {
          db.close();
          return;
        }
        dbRef.current = db;
        setDbReady(true);
        intervalId = window.setInterval(() => {
          flushQueue(false).catch((error) => logDebug('[Gallery] periodic flush error', error));
        }, FLUSH_INTERVAL_MS);
      } catch (error) {
        logDebug('[Gallery] failed to open IndexedDB', error);
      }
    })();

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
      flushQueue(true)
        .catch((error) => logDebug('[Gallery] flush on unmount failed', error))
        .then(() =>
          stopWorker().catch((error) => logDebug('[Gallery] stop worker on unmount failed', error)),
        )
        .finally(() => {
          try {
            dbRef.current?.close();
          } catch (error) {
            logDebug('[Gallery] failed to close IndexedDB', error);
          }
          dbRef.current = null;
        });
    };
  }, [CLIENT_ROI_MODE, flushQueue, stopWorker]);

  useEffect(() => {
    if (CLIENT_ROI_MODE) return;
    let active = true;

    (async () => {
      try {
        if (formIdRef.current && formIdRef.current > 0) {
          setFormDataId(formIdRef.current);
          return;
        }
        const ident = `run-${Math.floor(Date.now() / 1000)}`;
        const { formDataId: fdId } = await submitForm(ident);
        if (!active) return;
        formIdRef.current = fdId;
        setFormDataId(fdId);
        try {
          localStorage.setItem('iris_formDataId', String(fdId));
        } catch (error) {
          logDebug('[Gallery] failed to persist formDataId', error);
        }
      } catch (error) {
        if (!active) return;
        logDebug('[Gallery] failed to create formDataId', error);
        setErr('无法创建测验运行，请稍后重试');
      }
    })();

    return () => {
      active = false;
    };
  }, [CLIENT_ROI_MODE, setErr]);

  useEffect(() => {
    if (CLIENT_ROI_MODE) return;
    if (!STREAM_UPLOAD_DURING_TEST) return;
    if (!formDataId || formDataId <= 0) return;
    startWorker(formDataId);
  }, [CLIENT_ROI_MODE, formDataId, startWorker]);

  const clearZeroRecords = useCallback(async () => {
    const db = dbRef.current;
    if (!db) return;
    try {
      const tx = db.transaction('landmarks', 'readwrite');
      const store = tx.objectStore('landmarks');
      const index = store.index('by-formDataId');
      for (
        let cursor = await index.openCursor(IDBKeyRange.only(0));
        cursor;
        cursor = await cursor.continue()
      ) {
        const pk = cursor.primaryKey as number;
        await store.delete(pk);
      }
      await tx.done;
    } catch (error) {
      logDebug('[Gallery] failed to clear zero formDataId records', error);
    }
  }, []);

  const clearFormRecords = useCallback(async (fdId: number) => {
    if (!fdId) return;
    const db = dbRef.current;
    if (!db) return;
    try {
      const tx = db.transaction('landmarks', 'readwrite');
      const store = tx.objectStore('landmarks');
      const index = store.index('by-formDataId');
      for (
        let cursor = await index.openCursor(IDBKeyRange.only(fdId));
        cursor;
        cursor = await cursor.continue()
      ) {
        const pk = cursor.primaryKey as number;
        await store.delete(pk);
      }
      await tx.done;
    } catch (error) {
      logDebug('[Gallery] failed to clear records for formDataId', error);
    }
  }, []);

  useEffect(() => {
    if (CLIENT_ROI_MODE) return;
    if (!dbReady) return;
    if (!formDataId || formDataId <= 0) return;
    void clearZeroRecords();
    void clearFormRecords(formDataId);
  }, [CLIENT_ROI_MODE, dbReady, formDataId, clearZeroRecords, clearFormRecords]);

  useEffect(() => {
    let raf = 0;
    function loop() {
      const lm = landmarkerRef.current;
      const v = cam.current;
      if (lm && v && v.readyState >= 2 && items && items[idx] && items[idx].skipCapture !== true) {
        if (stream && !v.srcObject) {
          v.srcObject = stream;
        }
        if (v.paused) {
          v.play().catch((error) => logDebug('[Gallery] loop play resume failed', error));
        }
        const ts = performance.now();
        const res = lm.detectForVideo(v, ts);
        const faces = res.faceLandmarks ?? [];
        const bounds = computeFaceBoundsList(
          faces as unknown as Array<Array<{ x: number; y: number }>>,
        );
        const count = bounds.length;

        let selectedIndex: number | null = null;
        const anchor = anchorRef.current;
        if (count === 0) {
          selectedIndex = null;
        } else if (count === 1) {
          selectedIndex = 0;
          if (!anchor) {
            const nextAnchor = { cx: bounds[0].cx, cy: bounds[0].cy };
            anchorRef.current = nextAnchor;
            setSelectedFaceAnchor(nextAnchor);
          }
        } else {
          if (anchor) {
            selectedIndex = pickFaceIndexByAnchor(bounds, anchor);
          } else {
            selectedIndex = pickLargestFaceIndex(bounds);
            const nextAnchor = { cx: bounds[selectedIndex].cx, cy: bounds[selectedIndex].cy };
            anchorRef.current = nextAnchor;
            setSelectedFaceAnchor(nextAnchor);
          }
        }

        const ok = selectedIndex !== null;
        if (faceOkRef.current !== ok) {
          faceOkRef.current = ok;
          setFaceOK(ok);
        }

        if (ok) {
          anchorRef.current = { cx: bounds[selectedIndex!].cx, cy: bounds[selectedIndex!].cy };
        }

        if (CLIENT_ROI_MODE) {
          const pts = ok
            ? faces[selectedIndex!].map((p) => ({
                x: Number(p.x),
                y: Number(p.y),
                z: Number(p.z ?? 0),
              }))
            : [];
          updateRoiAccumulator(roiAccRef.current, idx, pts);
        } else if (ok) {
          const pts = faces[selectedIndex!].map((p) => ({
            x: Number(p.x),
            y: Number(p.y),
            z: Number(p.z ?? 0),
          }));

          const sample: EyeTraxSample = {
            timestamp: Math.round(ts),
            mediaIndex: idx,
            landmarks: pts,
          };
          addSample(sample);
          const fdId = formIdRef.current;
          if (fdId && fdId > 0) {
            const payload = {
              formDataId: fdId,
              mediaIndex: idx,
              landmarks: pts,
              timestamp: Date.now(),
            } as PendingRecord;
            queueRef.current.push(payload);
            if (CAPTURE_DEBUG) {
              console.debug('[capture-debug] enqueue sample', {
                mediaIndex: payload.mediaIndex,
                queueSize: queueRef.current.length,
              });
            }
            if (queueRef.current.length >= IDB_BATCH_SIZE) {
              flushQueue(false).catch((error) => logDebug('[Gallery] flushQueue error', error));
            }
          }
        }
      }
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [
    CLIENT_ROI_MODE,
    idx,
    items,
    addSample,
    stream,
    flushQueue,
    setFaceOK,
    setSelectedFaceAnchor,
  ]);

  useEffect(() => {
    if (!items || !mediaVid.current || !mediaImg.current) return;
    const it = items[idx];
    const v = mediaVid.current;
    const img = mediaImg.current;
    let rafId: number | null = null;
    const now = performance.now();
    startTimelineIfNeeded(now);

    // Stop any previous segment audio when we enter a new item.
    stopAudio();

    img.classList.remove('show');
    v.classList.remove('show');

    if (!it) return;

    if (it.type === 'image') {
      img.src = it.src;
      img.onload = () => {
        img.classList.add('show');
        if (it.audio) {
          void playAudio(it.audio, { volume: it.audioVolume });
        }
        if (it.skipCapture !== true) {
          startSegment(idx, performance.now());
        }
        const dur = Math.max(0, Number(it.duration) || 5000);
        const start = performance.now();
        const tick = (nowTs: number) => {
          if (nowTs - start >= dur) {
            if (it.skipCapture !== true) endActiveSegment(nowTs);
            setIdx((i) => i + 1);
            return;
          }
          rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
      };
    } else if (it.type === 'video') {
      v.classList.remove('bg-rotated');
      v.src = it.src;
      v.defaultMuted = false;
      v.muted = false;
      v.volume = 1;
      v.playsInline = true;
      (v as HTMLVideoElement & { webkitPlaysInline?: boolean }).webkitPlaysInline = true;
      v.autoplay = true;
      v.preload = 'auto';
      const syncVideoOrientation = () => {
        v.classList.toggle('bg-rotated', v.videoWidth > 0 && v.videoWidth < v.videoHeight);
      };
      const onLoadedMetadata = () => {
        syncVideoOrientation();
      };
      const onCanPlay = async () => {
        syncVideoOrientation();
        try {
          await v.play();
          v.classList.add('show');
          if (it.audio) {
            void playAudio(it.audio, { volume: it.audioVolume });
          }
          if (it.skipCapture !== true) startSegment(idx, performance.now());
        } catch (error) {
          logDebug('[Gallery] video autoplay failed', error);
        }
      };
      const onEnded = () => {
        if (it.skipCapture !== true) endActiveSegment(performance.now());
        setIdx((i) => i + 1);
      };

      v.addEventListener('loadedmetadata', onLoadedMetadata);
      v.addEventListener('canplay', onCanPlay);
      v.addEventListener('ended', onEnded);
      return () => {
        v.pause();
        v.classList.remove('bg-rotated');
        v.removeEventListener('loadedmetadata', onLoadedMetadata);
        v.removeEventListener('canplay', onCanPlay);
        v.removeEventListener('ended', onEnded);
        if (rafId) cancelAnimationFrame(rafId);
        stopAudio();
      };
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      stopAudio();
      if (it && it.skipCapture !== true) {
        endActiveSegment(performance.now());
      }
    };
  }, [items, idx, startTimelineIfNeeded, startSegment, endActiveSegment]);

  useEffect(() => {
    if (!items) return;
    if (idx < items.length) return;

    let cancelled = false;

    (async () => {
      if (CLIENT_ROI_MODE) {
        try {
          const summary = finalizeRoiAccumulator(roiAccRef.current);
          localStorage.setItem('iris_roi_summary', JSON.stringify(summary));
          localStorage.removeItem('argus_roi_summary');
        } catch (error) {
          logDebug('[Gallery] failed to persist roi summary', error);
        }
        await stopRecording();
        if (!cancelled) nav('/runner/done', { replace: true });
        return;
      }

      try {
        await flushQueue(true);
      } catch (error) {
        logDebug('[Gallery] failed to flush queue before navigate', error);
      }
      await stopWorker();
      await stopRecording();
      if (!cancelled) nav('/runner/done', { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [CLIENT_ROI_MODE, idx, items, nav, flushQueue, stopRecording, stopWorker]);

  if (err) {
    return (
      <div className="full center">
        <p className="error">无法启动画廊：{err}</p>
        <Button variant="contained" onClick={() => nav('/')}>
          返回
        </Button>
      </div>
    );
  }
  if (!items) {
    return (
      <div className="full center">
        <p>画廊加载中…</p>
      </div>
    );
  }

  const current = items[idx];
  const progressPct = items.length > 0 ? Math.round(((idx + 1) / items.length) * 100) : 0;
  const statusText = faceOK ? '信号正常' : '请保持脸部在画面中';

  return (
    <div className="stage" aria-label="Fullscreen gallery">
      <img ref={mediaImg} className={`bg ${current?.type === 'image' ? 'show' : ''}`} alt="" />
      <video
        ref={mediaVid}
        className={`bg ${current?.type === 'video' ? 'show' : ''}`}
        controls={false}
        disablePictureInPicture
        playsInline
        preload="auto"
      />

      <video ref={setCamRef} className="camHidden" playsInline muted />

      <div className="hud hud-top" aria-label="Test progress">
        <div className="hud-row">
          <span>
            进度 {idx + 1}/{items.length}
          </span>
          <span>{statusText}</span>
        </div>
        <div
          className="hud-bar"
          role="progressbar"
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i style={{ width: `${progressPct}%` }} />
        </div>
      </div>
    </div>
  );
}
